/**
 * Analytics module for PostHog tracking.
 * Uses the global window.posthog instance injected at build time.
 *
 * Naming convention: category:object_action
 * - category: context (slideshow, form, ui, app)
 * - object: component/location (slide, button, page)
 * - action: verb (view, click, submit, scroll)
 */

// PostHog types (minimal, for type safety)
interface PostHog {
  capture: (event: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  people: {
    set: (properties: Record<string, unknown>) => void;
  };
  reset: () => void;
}

declare global {
  interface Window {
    posthog?: PostHog;
  }
}

/**
 * Check if analytics should be enabled.
 * Skips tracking on localhost and internal hosts.
 */
function shouldTrack(): boolean {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname;

  // Skip tracking on development/internal hosts
  const internalHosts = ['localhost', '127.0.0.1', '::1'];
  if (internalHosts.includes(hostname)) return false;

  // Skip if hostname looks like a staging/internal domain
  if (hostname.includes('staging') || hostname.includes('dev.')) return false;

  return true;
}

/**
 * Get the PostHog instance (injected via build-time snippet).
 */
function getPostHog(): PostHog | null {
  if (!shouldTrack()) return null;
  return window.posthog || null;
}

/**
 * Track a custom event with properties.
 * @param event - Event name using category:object_action convention
 * @param properties - Additional event properties
 */
export function trackEvent(event: string, properties: Record<string, unknown> = {}): void {
  const posthog = getPostHog();
  if (!posthog) return;

  posthog.capture(event, {
    ...properties,
    // Add common properties
    url: window.location.href,
    referrer: document.referrer || undefined,
  });
}

/**
 * Track slide view event.
 * Event: slideshow:slide_view
 */
export function trackSlideView(
  slideId: string,
  slideIndex: number,
  source: string = 'unknown'
): void {
  trackEvent('slideshow:slide_view', {
    slide_id: slideId,
    slide_index: slideIndex,
    source,
  });
}

/**
 * Track button click event.
 * Event: ui:button_click
 */
export function trackButtonClick(
  buttonType: string,
  properties: Record<string, unknown> = {}
): void {
  trackEvent('ui:button_click', {
    button_type: buttonType,
    ...properties,
  });
}

/**
 * Track scroll depth event.
 * Event: ui:scroll_depth
 */
export function trackScroll(depth: number, slideId: string): void {
  trackEvent('ui:scroll_depth', {
    scroll_depth: depth,
    slide_id: slideId,
  });
}

/**
 * Track time spent on slide.
 * Event: slideshow:time_spent
 */
export function trackTimeOnSlide(slideId: string, durationSeconds: number): void {
  trackEvent('slideshow:time_spent', {
    slide_id: slideId,
    duration_seconds: durationSeconds,
  });
}

/**
 * Track form submission.
 * Event: form:submit
 */
export function trackFormSubmit(formType: string, properties: Record<string, unknown> = {}): void {
  trackEvent('form:submit', {
    form_type: formType,
    ...properties,
  });
}

/**
 * Initialize analytics (no-op - snippet handles init).
 * Kept for backwards compatibility.
 */
export function initAnalytics(): void {
  // PostHog is initialized via build-time snippet injection.
  // This function is kept for backwards compatibility but does nothing.
  const posthog = getPostHog();

  // Set user properties via $set in capture (posthog.people.set is deprecated)
  if (posthog) {
    posthog.capture('$set', {
      $set: {
        initial_referrer: document.referrer || 'direct',
        user_language: navigator.language,
      },
    });
  }

  // Track initial page view
  trackEvent('app:page_view', {
    page_title: document.title,
  });
}
